/**
 * Monitoring read + its refresh policy, and the staff capability check.
 *
 * Monitoring is an expensive aggregate over an acceptance cohort, so the refresh
 * rules are part of the contract rather than a nicety: one request in flight at
 * a time, auto-refresh no more than once every two minutes, paused entirely
 * while the tab is hidden, and one refresh when it becomes visible again.
 * Firestore hints never touch it.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES, QUERY_KEYS } from "@/alerts/api/config.ts";
import { unwrapEnvelope, type ApiEnvelope } from "@/alerts/api/envelope.ts";
import { isAlertsMockMode } from "@/alerts/api/mocks/alerts.mocks.ts";
import { getAlertMonitoring } from "@/alerts/api/requests/alerts-monitoring.requests.ts";
import {
  MONITORING_REFRESH_MS,
  type AlertMonitoringParams,
  type AlertMonitoringResponse,
} from "@/alerts/types/alerts-monitoring.ts";

interface CurrentUser {
  id: number;
  is_staff: boolean;
}

const getCurrentUser = async (): Promise<CurrentUser> => {
  if (isAlertsMockMode()) {
    // Staff by default on fixtures so the surface is previewable; flip with
    // VITE_ALERTS_MOCK_STAFF=false to check the member-facing behaviour.
    const env = import.meta.env as Record<string, string | undefined>;
    return { id: 1, is_staff: env.VITE_ALERTS_MOCK_STAFF !== "false" };
  }
  const response = await axiosClient.get<
    unknown,
    AxiosResponse<ApiEnvelope<CurrentUser, unknown>>
  >(API_ROUTES.CURRENT_USER);
  return unwrapEnvelope(response).data;
};

export const useCurrentUser = () =>
  useQuery<CurrentUser>({
    queryKey: [QUERY_KEYS.ALERTS_CURRENT_USER],
    queryFn: getCurrentUser,
    staleTime: 5 * 60_000,
  });

/**
 * Staff gate for NAV AND ROUTE VISIBILITY ONLY — the backend authorizes
 * `/api/monitoring/alerts/` independently and remains authoritative. Defaults to
 * false until `/users/me/` resolves, so the surface never flashes for a
 * non-staff member.
 */
export const useIsStaff = (): boolean => useCurrentUser().data?.is_staff ?? false;

export const useAlertMonitoring = (params: AlertMonitoringParams | null, enabled: boolean) =>
  useQuery<AlertMonitoringResponse>({
    queryKey: [QUERY_KEYS.ALERTS_MONITORING, params],
    queryFn: ({ signal }) => getAlertMonitoring(params as AlertMonitoringParams, signal),
    enabled: enabled && params != null,
    refetchInterval: MONITORING_REFRESH_MS,
    // Explicitly NOT in the background: a hidden tab must not keep pulling an
    // expensive aggregate nobody is looking at.
    refetchIntervalInBackground: false,
    // The single refresh on return to the tab.
    refetchOnWindowFocus: true,
    // A background failure must never wipe the dashboard already on screen.
    placeholderData: keepPreviousData,
    // Each failure kind has its own recovery (see classifyMonitoringError);
    // blind retries would hammer an invalid request or a throttled endpoint.
    retry: false,
  });
